import { datePresetCond } from './filters.js';
import { sanitizeLayout, resolveRowState, LAYOUT_COLUMN_KEYS, LAYOUT_OPTION_KEYS, MAX_SCROLL_PX } from './grid-util.js';
export class GridLayoutState {
    setSort(keys) { this.sortKeys = keys; this.renderHeader(); this.rebuild(); }
    /** @internal */ withSortText(keys) {
        if (!keys.length)
            return keys;
        return keys.map(k => {
            const c = this.colInfo(k.col);
            if (!c || c.special)
                return k;
            const d = c.def;
            if (!(d.sortByDisplayText ?? (!!this.opts.sortByDisplayText && !!d.items)))
                return k;
            const items = d.items ? this.itemsOf(c) : null;
            let e = this.textSortFns.get(k.col);
            if (!e || e.def !== d || e.items !== items) {
                const cc = c;
                e = { def: d, items, fn: v => this.displayText(cc, v, -1) };
                this.textSortFns.set(k.col, e);
            }
            return { ...k, text: e.fn };
        });
    }
    /** 머리글 정렬 표시만 (보기 순서는 밖에서 정할 때 — setExternalOrder) */
    setSortIndicator(keys) { this.sortKeys = keys; this.renderHeader(); }
    /**
     * 보기 순서를 밖에서 정함 (Nexacro 호환: 소계 행·insertRow 위치를 그대로). items: 0 이상 = 물리 행, 음수 = 소계 줄 -(k+1) — 값은 sub(k, 컬럼).
     * 그리드 정렬·그룹은 쓰지 않고, 그리드 필터는 그 위에 거름. null 이면 해제
     */
    setExternalOrder(items, sub) {
        this.extOrder = items ? { items: Int32Array.from(items), sub } : null;
        this.rebuild();
    }
    // ── 레이아웃 ──
    getLayout() {
        const columns = this.opts.columns.map(c => {
            const o = {};
            for (const k of LAYOUT_COLUMN_KEYS) {
                const v = c[k];
                if (v !== undefined && typeof v !== 'function' && !(k === 'groupInterval' && typeof v !== 'string'))
                    o[k] = v;
            }
            return o;
        });
        const options = {};
        for (const k of LAYOUT_OPTION_KEYS) {
            const v = this.opts[k];
            if (v !== undefined)
                options[k] = v;
        }
        const rows = this.getRowState();
        const filters = this.opts.saveFilters ? this.getFilterState() : undefined;
        return { v: 1, columns, options, sort: [...this.sortKeys].map(k => ({ col: k.col, dir: k.dir })), groupBy: this.groupCols(), headerGroups: this.opts.headerGroups, ...(this.opts.rowLayout ? { rowLayout: this.opts.rowLayout } : {}), ...(rows ? { rows } : {}), ...(filters ? { filters } : {}) };
    }
    /** 지금 필터 상태 (레이아웃 저장용): 필터 행·머리글 필터·빌더·켜기/끄기 */
    getFilterState() {
        const o = {};
        if (this.filterText.size)
            o.row = [...this.filterText];
        if (this.menuFilters.size)
            o.menu = this.getColumnFilters();
        if (this.advFilter)
            o.adv = this.advFilter;
        if (!this.filterEnabled)
            o.enabled = false;
        return o;
    }
    /** 필터 상태 적용 (레이아웃 복원). 없는 컬럼은 건너뜀, 날짜 빠른 선택은 오늘 기준으로 다시 계산 */
    setFilterState(f) {
        const known = (c) => !!this.colInfo(c) || !!this.ds.cols[c];
        this.applyingFilters = true;
        try {
            this.filterText = new Map((f.row ?? []).filter(([c]) => known(c)));
            this.menuFilters.clear();
            for (const [c, cf] of Object.entries(f.menu ?? {}))
                if (known(c)) {
                    const cond = cf.cond?.preset ? { ...datePresetCond(cf.cond.preset), preset: cf.cond.preset } : cf.cond;
                    if (cf.values || cond)
                        this.menuFilters.set(c, { values: cf.values ? new Set(cf.values) : undefined, cond });
                }
            this.advFilter = f.adv ?? null;
            this.filterEnabled = f.enabled !== false;
            this.syncFilterInputs();
            this.renderHeader();
            this.rebuild();
        }
        finally {
            this.applyingFilters = false;
        }
        this.filterSig = JSON.stringify(this.getFilterState());
    }
    /** 행 화면 상태(높이·숨김·고정·잠금 셀·보호)를 행 키로 — keyCol 이 없으면 undefined (물리 행 번호는 다시 불러오면 바뀜) */
    getRowState() {
        const kc = this.ds.keyCol;
        if (!kc)
            return undefined;
        const key = (r) => { const v = r >= 0 && r < this.ds.length ? this.ds.keyOf(r) : null; return v == null || v === '' ? null : String(v); };
        const ks = (rs) => { const o = []; for (const r of rs) {
            const k = key(r);
            if (k != null)
                o.push(k);
        } return o; };
        const heights = [], locked = [], unlocked = [];
        for (const [r, h] of this.rowHts) {
            const k = key(r);
            if (k != null)
                heights.push([k, h]);
        }
        for (const [rc, v] of this.cellLock) {
            const i = rc.indexOf(':'), k = key(+rc.slice(0, i));
            if (k != null)
                (v ? locked : unlocked).push([k, rc.slice(i + 1)]);
        }
        const o = {};
        if (heights.length)
            o.heights = heights;
        if (this.hiddenRowSet.size)
            o.hidden = ks(this.hiddenRowSet);
        if (this.pinList[0].length)
            o.pinTop = ks(this.pinList[0]);
        if (this.pinList[1].length)
            o.pinBottom = ks(this.pinList[1]);
        if (locked.length)
            o.locked = locked;
        if (unlocked.length)
            o.unlocked = unlocked;
        if (this.protectOn)
            o.protect = true; // 보호 해제 상태는 적지 않음 (초기화로 보호가 풀리지 않게)
        return Object.keys(o).length ? o : undefined; // 비었으면 생략 → resetLayout(처음 화면)이 코드로 정한 잠금·고정을 지우지 않음
    }
    /** 행 화면 상태 적용 (키 → 물리 행). 지금 자료에 없는 키가 있으면 다음 load 때 한 번 더 적용. 보호 해제는 암호 없는 보호만 */
    applyRowState(rs, fromLoad = false) {
        const kc = this.ds.keyCol;
        if (!kc)
            return;
        const x = resolveRowState(rs, k => this.ds.findByKey(k));
        this.rowStateQuiet = true;
        try {
            this.rowHts = x.heights;
            this.hiddenRowSet = new Set(x.hidden);
            this.pinList = [x.pinTop, x.pinBottom];
            this.cellLock = x.locks;
            if (x.protect === true && !this.protectOn)
                this.protect();
            else if (x.protect === false && this.protectOn)
                this.unprotect();
        }
        finally {
            this.rowStateQuiet = false;
        }
        this.pendRows = !this.ds.length || (x.missing.length && !fromLoad) ? rs : null;
        this.pool.forEach(p => (p.key = ''));
        this.rebuild();
    }
    /** 행 화면 상태가 바뀜 → 레이아웃 알림 (keyCol 이 있을 때만) */
    /** @internal */ rowStateChanged() { if (this.rowStateQuiet)
        return; this.pendRows = null; if (this.ds.keyCol)
        this.notifyLayout(); }
    /** 레이아웃의 컬럼 순서·속성을 컬럼 정의에 입힘 (함수 속성은 정의 것 유지) */
    /** @internal */ mergeLayoutColumns(defs, l) {
        const byId = new Map(defs.map(c => [c.id, c]));
        const used = new Set();
        const cols = [];
        for (const lc of l.columns) {
            if (!lc.id)
                continue;
            const base = byId.get(lc.id) ?? { id: lc.id };
            const merged = { ...base };
            for (const k of LAYOUT_COLUMN_KEYS)
                if (k in lc)
                    merged[k] = lc[k];
            for (const k of LAYOUT_COLUMN_KEYS)
                if (!(k in lc) && k !== 'id' && base[k] !== undefined && typeof base[k] !== 'function' && l.v === 1 && (k === 'hidden' || k === 'pinned'))
                    delete merged[k];
            cols.push(merged);
            used.add(lc.id);
        }
        for (const c of defs)
            if (!used.has(c.id))
                cols.push(c);
        return cols;
    }
    /** 레이아웃 적용. 함수 속성(items·validate·value…)은 기존 컬럼 정의에서 유지 */
    applyLayout(l, notify = true) {
        const clean = sanitizeLayout(l);
        if (!clean)
            return;
        l = clean;
        const byId = new Map(this.opts.columns.map(c => [c.id, c]));
        const cols = this.mergeLayoutColumns(this.opts.columns, l);
        this.layoutTouched = true;
        const opts = {};
        for (const k of LAYOUT_OPTION_KEYS)
            if (l.options && k in l.options)
                opts[k] = l.options[k];
        this.sortKeys = (l.sort ?? []).filter(k => byId.has(k.col) || this.ds.cols[k.col]);
        this.opts.groupBy = l.groupBy ?? null;
        // 멀티로우 배치(칸 맞바꿈·너비)는 멀티로우 화면이고 모든 칸의 컬럼이 있을 때만
        if (l.rowLayout && this.opts.rowLayout && l.rowLayout.cells.every(x => byId.has(x.col)))
            this.opts.rowLayout = l.rowLayout;
        Object.assign(this.opts, opts);
        this.setColumns(cols, l.headerGroups ?? this.opts.headerGroups);
        this.renderGroupPanel();
        if (l.rows)
            this.applyRowState(l.rows);
        // 필터 (saveFilters): 예전 레이아웃(filters 없음)은 지금 필터 그대로, 있으면 그것으로
        if (this.opts.saveFilters && l.filters)
            this.setFilterState(l.filters);
        if (notify)
            this.notifyLayout();
    }
    /** 처음 화면 설정으로 */
    resetLayout() {
        if (this.initialLayout)
            this.applyLayout(this.opts.saveFilters ? { ...this.initialLayout, filters: this.initialLayout.filters ?? {} } : this.initialLayout);
        this.layoutTouched = false;
        const key = this.opts.layoutKey, st = this.opts.layoutStore;
        if (!key)
            return;
        if (st) {
            clearTimeout(this.layoutTimer);
            this.layoutTimer = 0;
            (st.remove ? st.remove(key) : st.save(key, this.initialLayout)).catch(() => { });
        }
        else
            try {
                localStorage.removeItem('onegrid:' + key);
            }
            catch { /* 무시 */ }
    }
    /** 서버 저장소에서 레이아웃을 다 불러올 때까지 (저장소가 없으면 바로) */
    whenLayoutLoaded() { return this.layoutLoading; }
    /** 미뤄 둔 레이아웃 저장을 지금 보냄 */
    flushLayout() {
        if (!this.layoutTimer)
            return Promise.resolve();
        clearTimeout(this.layoutTimer);
        this.layoutTimer = 0;
        return this.opts.layoutStore.save(this.opts.layoutKey, this.getLayout()).catch(() => { });
    }
    notifyLayout() {
        const l = this.getLayout();
        this.layoutGen++;
        this.layoutTouched = true;
        const key = this.opts.layoutKey, st = this.opts.layoutStore;
        if (key && st) {
            clearTimeout(this.layoutTimer);
            this.layoutTimer = setTimeout(() => { this.layoutTimer = 0; st.save(key, this.getLayout()).catch(() => { }); }, this.opts.layoutSaveDelay ?? 600);
        }
        else if (key)
            try {
                localStorage.setItem('onegrid:' + key, JSON.stringify(l));
            }
            catch { /* 무시 */ }
        this.opts.onLayoutChange?.(l);
    }
    /** 컬럼 하나의 속성 변경 (RealGrid setColumnProperty) */
    setColumnProperty(col, prop, value) {
        const cols = this.opts.columns.map(c => (c.id === col ? { ...c, [prop]: value } : c));
        this.setColumns(cols, this.opts.headerGroups);
        this.notifyLayout();
    }
    getColumnProperty(col, prop) { return this.opts.columns.find(c => c.id === col)?.[prop]; }
    /** 컬럼 순서 이동. 대상 컬럼의 고정 상태를 따른다 */
    moveColumn(col, target, after = false) {
        if (col === target)
            return;
        const cols = [...this.opts.columns];
        const i = cols.findIndex(c => c.id === col);
        if (i < 0)
            return;
        const [m] = cols.splice(i, 1);
        const t = cols.findIndex(c => c.id === target);
        if (t < 0)
            return;
        const tgt = cols[t];
        cols.splice(t + (after ? 1 : 0), 0, { ...m, pinned: tgt.pinned });
        this.setColumns(cols, this.opts.headerGroups);
        this.notifyLayout();
    }
    setColumnVisible(col, visible) { this.setColumnProperty(col, 'hidden', !visible); }
    /** 내용에 맞춘 너비 (헤더 + 보이는 순서 앞 1000행 기준). col 생략 = 전체 */
    bestFit(col) {
        const ctx = document.createElement('canvas').getContext('2d');
        const cs = getComputedStyle(this.root);
        ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        const bold = `700 ${cs.fontSize} ${cs.fontFamily}`;
        const targets = this.cols.filter(c => !c.special && (!col || c.id === col));
        const sample = Math.min(this.view.length, 1000);
        const cols = this.opts.columns.map(d => {
            const c = targets.find(x => x.id === d.id);
            if (!c)
                return d;
            ctx.font = bold;
            let w = ctx.measureText(d.header ?? d.id).width + 22 + (this.opts.columnFilterMenu !== false ? 18 : 0);
            ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
            for (let i = 0; i < sample; i++) {
                const r = this.view[i];
                w = Math.max(w, ctx.measureText(this.displayText(c, this.valueOf(c, r), r)).width + 16);
            }
            return { ...d, width: Math.round(Math.min(600, Math.max(40, w))) };
        });
        this.setColumns(cols, this.opts.headerGroups);
        this.notifyLayout();
    }
    /** 행 높이 지정 (px, null 이면 기본). 행 번호·표시기·상태 컬럼 아래 경계를 끌어도 바뀜 (rowResize) */
    setRowHeight(row, px, notify = true) {
        if (px == null)
            this.rowHts.delete(row);
        else
            this.rowHts.set(row, Math.max(16, Math.min(400, Math.round(px))));
        this.buildOffsets();
        this.sizer.style.height = Math.min(this.totalH(), MAX_SCROLL_PX) + 'px';
        this.pool.forEach(p => (p.key = ''));
        this.requestRender(true);
        if (notify)
            this.rowStateChanged();
    }
    getRowHeight(row) { const vi = this.disp.indexOf(row); return vi >= 0 ? this.rowH(vi) : this.rowHts.get(row) ?? this.rh; }
    /** 행 숨기기 (필터와 별개, 데이터·저장은 그대로) */
    hideRows(rows) { for (const r of rows)
        this.hiddenRowSet.add(r); this.rebuild(); this.rowStateChanged(); }
    /** 숨긴 행 다시 보이기 (생략하면 전부) */
    showRows(rows) { if (rows)
        for (const r of rows)
            this.hiddenRowSet.delete(r);
    else
        this.hiddenRowSet.clear(); this.rebuild(); this.rowStateChanged(); }
    getHiddenRows() { return [...this.hiddenRowSet]; }
    /** @internal */ startRowResize(row, vi, e) {
        const y0 = e.clientY, h0 = this.rowH(vi);
        this.root.style.cursor = 'row-resize';
        const move = (ev) => this.setRowHeight(row, h0 + ev.clientY - y0, false);
        const up = () => { this.root.style.cursor = ''; this.rowStateChanged(); window.removeEventListener('mousemove', move, true); window.removeEventListener('mouseup', up, true); };
        window.addEventListener('mousemove', move, true);
        window.addEventListener('mouseup', up, true);
    }
    /** 접을 수 있는 컬럼 그룹 접기·펴기 (collapsed 생략하면 뒤집기) */
    toggleColumnGroup(header, collapsed) {
        const now = this.grpCollapsed.has(header), next = collapsed ?? !now;
        if (next === now)
            return;
        if (next)
            this.grpCollapsed.add(header);
        else
            this.grpCollapsed.delete(header);
        this.layout();
        this.requestRender(true);
    }
    isColumnGroupCollapsed(header) { return this.grpCollapsed.has(header); }
    /** 비율 너비 고정 해제 (사용자가 끌어 바꾼 너비를 버리고 다시 맞춤) */
    resetFlex() { this.flexFixed.clear(); for (const c of this.cols)
        if (!c.special)
            c.width = c.def.width ?? 100; this.flexAvail = -1; this.refitIfResized(); this.requestRender(true); }
    /** 행 고정 API: 물리 행을 위('top')·아래('bottom') 고정 상자에 표시, null 이면 해제. 본문에서도 그대로 보임 (필터로 숨으면 고정 상자에서도 빠짐) */
    pinRow(row, side) {
        for (const l of this.pinList) {
            const i = l.indexOf(row);
            if (i >= 0)
                l.splice(i, 1);
        }
        if (side)
            this.pinList[side === 'top' ? 0 : 1].push(row);
        this.requestRender(true);
        this.rowStateChanged();
    }
    getPinnedRows() { return { top: [...this.pinList[0]], bottom: [...this.pinList[1]] }; }
    clearPinnedRows() { this.pinList = [[], []]; this.requestRender(true); this.rowStateChanged(); }
}
